import { motion } from "framer-motion";
import { LogIn, ShieldAlert } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";

type RoleType = "admin" | "vendor" | "moderator" | "product_editor";

interface AuthGuardProps {
  children: React.ReactNode;
  /** Optional: require a specific role or any of multiple roles */
  requireRole?: RoleType | RoleType[];
}

const AuthGuard = ({ children, requireRole }: AuthGuardProps) => {
  const { user, loading, isAdmin, isVendor, isModerator, isProductEditor } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="space-y-4 w-full max-w-md px-4">
          <Skeleton className="h-12 w-12 rounded-xl mx-auto" />
          <Skeleton className="h-4 w-32 mx-auto" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4" dir="rtl">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center space-y-5 max-w-sm"
        >
          <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
            <LogIn size={36} className="text-primary" />
          </div>
          <h1 className="text-2xl font-black text-foreground">נדרשת התחברות</h1>
          <p className="text-muted-foreground leading-relaxed">
            עליך להתחבר לחשבון שלך כדי לגשת לעמוד זה
          </p>
          <div className="flex flex-col gap-3">
            <Button
              onClick={() => navigate("/כניסה")}
              className="gradient-primary text-primary-foreground w-full gap-2"
              size="lg"
            >
              <LogIn size={18} />
              התחבר עכשיו
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/")}
              className="w-full"
            >
              חזרה לחנות
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (requireRole) {
    const roles = Array.isArray(requireRole) ? requireRole : [requireRole];
    const roleMap: Record<RoleType, boolean> = { admin: isAdmin, vendor: isVendor, moderator: isModerator, product_editor: isProductEditor };
    // Admin always has access to everything
    const hasAccess = isAdmin || roles.some((r) => roleMap[r]);

    if (!hasAccess) {
      // Show vendor-specific message if vendor is one of the required roles
      if (roles.includes("vendor") && roles.length === 1) {
        return (
          <div className="min-h-screen bg-background flex items-center justify-center px-4" dir="rtl">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center space-y-5 max-w-sm"
            >
              <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
                <ShieldAlert size={36} className="text-primary" />
              </div>
              <h1 className="text-2xl font-black text-foreground">אין גישה לאזור הספקים</h1>
              <p className="text-muted-foreground leading-relaxed">
                עליך להירשם כספק כדי לגשת לעמוד זה
              </p>
              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => navigate("/הפוך-לספק")}
                  className="gradient-primary text-primary-foreground w-full"
                >
                  הפוך לספק
                </Button>
                <Button variant="outline" onClick={() => navigate("/")} className="w-full">
                  חזרה לחנות
                </Button>
              </div>
            </motion.div>
          </div>
        );
      }

      return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4" dir="rtl">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center space-y-5 max-w-sm"
          >
            <div className="w-20 h-20 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto">
              <ShieldAlert size={36} className="text-destructive" />
            </div>
            <h1 className="text-2xl font-black text-foreground">אין גישה</h1>
            <p className="text-muted-foreground leading-relaxed">
              אין לך הרשאות מתאימות לצפייה בעמוד זה
            </p>
            <Button variant="outline" onClick={() => navigate("/")} className="w-full">
              חזרה לחנות
            </Button>
          </motion.div>
        </div>
      );
    }
  }

  return <>{children}</>;
};

export default AuthGuard;
